/**
 *  This is the main file of PDF reader
 *
 *  libraries:
 *
 *  * PDF.js
 *  https://mozilla.github.io/pdf.js/
 *
 *  * React-konva
 *  https://github.com/lavrton/react-konva
 *  https://konvajs.github.io/api/index.html
 *
 *  just pass the props and wait for render
 *
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { PDFPageView } from 'pdfjs-dist/web/pdf_viewer'; // PDF.js
import './css/styles.css'; // custom styles
import { Stage, Layer, Rect, Text, Circle, Group, Line, Path } from 'react-konva'; // canvas library
import uuid from './helpers/uuid'; // helper to create UUID for new annotations
import ClearAllModal from '../ClearAllModal';
import Konva from 'konva';

class PdfAnno extends React.Component {
    constructor() {
        super();
        this.state = {
            blob: null,
            documentHeight: 0,
            documentWidth: 0,
            documentLoaded: false,
            currentTool: 3,
            initCursorX: 0, // start X position when touchStart or mouseDown event happens
            initCursorY: 0, // start Y position when touchStart or mouseDown event happens
            cursorX: 0, // current X position
            cursorY: 0, // current Y position
            isMousePressed: false,
            annotations: [],
            selectedItem: null,
            resizing: false,
            resizingLeft: false,
            currentPath: '',
            pathWrapper: null,
            isNoteEditing: false,
            pages: 0,
            history: [JSON.stringify([])], // should be in a String
            stepsBack: 0,
            showPDF: false,
            showClearAllModal: false
        };
    }

    static propTypes = {
        page: PropTypes.number,
        scale: PropTypes.number,
        rotate: PropTypes.number,
        blob: PropTypes.string,
        highlightColor: PropTypes.string,
        penColor: PropTypes.string,
        annotations: PropTypes.array,
        onChangeAnnotations: PropTypes.func,
        className: PropTypes.string,
        currentTool: PropTypes.number,
        fileName: PropTypes.string,
    };

    static defaultProps = {
        page: 1,
        scale: 1.0,
        rotate: 0,
        highlightColor: 'rgba(255,255,0, 0.3)',
        penColor: 'red',
        currentTool: 2,
        fileName: 'pdf-file',
    };

    componentWillMount() {
        let { annotations, currentTool } = this.props;
        if (annotations)
            this.setState({ annotations, history: [JSON.stringify(annotations)], currentTool });
    }

    componentDidMount() {
        window.addEventListener('keydown', this.documentOnKeyDown); //listen for keyboard pressing
        this.onDocumentReady(this.props.blob);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.documentOnKeyDown);
    }

    componentWillReceiveProps(newProps) {
        if (
            newProps.blob !== this.props.blob ||
            newProps.scale !== this.props.scale ||
            newProps.page !== this.props.page
        ) {
            if (newProps.blob !== this.props.blob) {
                this.setState({
                    annotations: newProps.annotations,
                });
            }

            this.setState({ documentLoaded: false });
            this.onDocumentReady(newProps.blob); // if props change, we render everything again
        }
    }

    /**
     * create a page
     *
     */
    onDocumentReady = blob => {
        let container = document.getElementById('pageContainer');
        let page = document.getElementsByClassName('page')[0];
        if (page) {
            container.removeChild(page);
        }
        window.PDFJS
            .getDocument(blob)
            .then(pdfDocument => {

                // Document loaded, retrieving the page.
                // console.log('pdfDocument ', pdfDocument);
                this.pdfDocument = pdfDocument;
                pdfDocument.getPage(this.props.page).then(pdfPage => {
                    // Creating the page view with default parameters.
                    // console.log('pdfPage ', pdfPage);
                    // console.log('viewport ', pdfPage.getViewport(this.props.scale));
                    let pdfPageView = new window.PDFJS.PDFPageView({
                        container: container,
                        id: this.props.page,
                        scale: this.props.scale * 0.75, // use here because of the previous sizes of the viewer
                        defaultViewport: pdfPage.getViewport(this.props.scale * 0.75), // use here because of the previous sizes of the viewer
                    });

                    // Associates the actual page with the view, and drawing it
                    pdfPageView.setPdfPage(pdfPage);
                    pdfPageView.draw();
                    // set info for rendering canvas annotation level
                    // console.log('pdfPageView ', pdfPageView);
                    this.setState({
                        documentLoaded: true,
                        documentHeight: pdfPageView.height,
                        documentWidth: pdfPageView.width,
                        pages: pdfDocument.numPages,
                        selectedItem: null,
                    });
                });
            })
            .catch(e => console.log('PDFAnno error', e));
    };

    // keypress handler
    documentOnKeyDown = e => {
        if (e.keyCode === 27) {
            this.setState({ selectedItem: null, isMousePressed: false });
            this.canvasClick = false;
        }
        if (e.keyCode === 46 && this.state.selectedItem) {
            // on Delete we delete the current selected
            this.deleteAnnotationByUUID(this.state.selectedItem.uuid);
        }
    };

    // changing annotations handler (+history)
    onChangeAnnotations = notChangeHistory => {
        let { onChangeAnnotations } = this.props;

        let annotations = this.state.annotations.slice();

        if (onChangeAnnotations) {
            if (notChangeHistory) {
                return onChangeAnnotations(annotations);
            }

            let history = this.state.history;
            let stepsBack = this.state.stepsBack;
            let last = history[history.length - 1];

            if (stepsBack > 0) {
                history = history.slice(0, history.length - stepsBack);
            }

            if (last === JSON.stringify(annotations)) {
                return;
            }
            history.push(JSON.stringify(annotations));
            this.setState({ history: history.slice(), stepsBack: 0 });
            onChangeAnnotations(annotations);
        }
    };

    // back in a history
    undo = () => {
        let history = this.state.history;
        let historyLength = history.length;
        let stepsBack = this.state.stepsBack;
        // console.log('PDFAnno undo', historyLength, stepsBack);
        if (historyLength > 1 && stepsBack < historyLength - 1) {
            stepsBack += 1;
            let historyIndex = historyLength - 1 - stepsBack;
            // console.log('historyIndex', historyIndex);
            try {
                let annotations = JSON.parse(history[historyIndex]);
                // console.log('undo', annotations);
                this.setState(
                    { annotations, stepsBack, selectedItem: null },
                    this.onChangeAnnotations.bind(this, true),
                );
            } catch (e) {
                console.log('JSON.parse error', e);
            }
        }
    };

    // forward in a history
    redo = () => {
        let history = this.state.history;
        let historyLength = history.length;
        let stepsBack = this.state.stepsBack;
        // console.log('PDFAnno undo', historyLength, stepsBack);
        if (historyLength > 1 && stepsBack > 0) {
            stepsBack -= 1;
            let historyIndex = historyLength - 1 - stepsBack;
            // console.log('historyIndex', historyIndex);
            try {
                let annotations = JSON.parse(history[historyIndex]);
                // console.log('undo', annotations);
                this.setState(
                    { annotations, stepsBack, selectedItem: null },
                    this.onChangeAnnotations.bind(this, true),
                );
            } catch (e) {
                console.log('JSON.parse error', e);
            }
        }
    };

    deleteAnnotationByUUID = uuid => {
        let annotations = this.state.annotations.filter(item => {
            return item.uuid !== uuid;
        });
        // saveHook
        this.setState(
            { annotations, selectedItem: null, isNoteEditing: false },
            this.onChangeAnnotations,
        );
    };

    pageOnMouseDown = e => {
        let clientX = e.clientX;
        let clientY = e.clientY;
        this.setInitialClick(clientX, clientY);
    };

    pageOnTouchStart = e => {
        let clientX = e.nativeEvent.touches[0].clientX;
        let clientY = e.nativeEvent.touches[0].clientY;
        this.setInitialClick(clientX, clientY);
    };

    /**
     * get initial coordinates
     * @param clientX
     * @param clientY
     */
    setInitialClick = (clientX, clientY) => {
        let page = document.getElementById('pageContainer', this.canvasClick);
        if (!this.canvasClick) this.setState({ selectedItem: null });

        if (this.state.currentTool === 1 || this.state.currentTool === 2) {
            this.addDrawingCursor();
        }

        let cursorX = clientX - page.getBoundingClientRect().left;
        let cursorY = clientY - page.getBoundingClientRect().top;
        let scale = this.props.scale;
        let currentPath = '';
        if (this.state.currentTool === 2) {
            let x = cursorX / scale;
            let y = cursorY / scale;
            currentPath = 'M ' + x + ' ' + y;
        }
        this.setState({
            isMousePressed: true,
            cursorX,
            cursorY,
            initCursorX: cursorX,
            initCursorY: cursorY,
            currentPath,
        });
    };

    pageOnMouseUp = e => {
        // console.log('PDFAnno pageOnMouseUp');
        if (
            !this.canvasClick &&
            this.state.selectedItem &&
            !this.state.resizing &&
            !this.state.resizingLeft
        ) {
            this.setState({ selectedItem: null });
        }
        this.canvasClick = false;
        if (this.state.currentTool === 1 || this.state.currentTool === 2) {
            this.removeDrawingCursor();
        }

        if (this.state.resizing) {
            // console.log('resizing end');
            return this.setState(
                { resizing: false, isMousePressed: false },
                this.onChangeAnnotations,
            );
        }

        if (this.state.resizingLeft) {
            // console.log('resizingLeft end');
            return this.setState(
                { resizingLeft: false, isMousePressed: false },
                this.onChangeAnnotations,
            );
        }

        if (this.state.isMousePressed) {
            let scale = this.props.scale;
            let isMousePressed = false;
            let annotations = this.state.annotations;
            switch (this.state.currentTool) {
                case 1:
                    let width = this.state.cursorX - this.state.initCursorX;
                    let height = this.state.cursorY - this.state.initCursorY;
                    let x = this.state.cursorX;
                    let y = this.state.cursorY;

                    if (width > 0) {
                        x = this.state.initCursorX / scale;
                    }
                    if (height > 0) {
                        y = this.state.initCursorY / scale;
                    }
                    height = Math.abs(height) / scale;
                    width = Math.abs(width) / scale;
                    if (width > 0 && height > 0) {
                        let newItem = {
                            type: 'area',
                            x,
                            y,
                            width,
                            height,
                            fill: this.props.highlightColor,
                            uuid: uuid(),
                            page: this.props.page,
                        };
                        annotations.push(newItem);
                        // console.log('PDFAnno pageOnMouseUp', annotations);
                    }

                    let cursorX = 0,
                        initCursorX = 0;
                    let cursorY = 0,
                        initCursorY = 0;

                    this.setState(
                        {
                            cursorX,
                            cursorY,
                            isMousePressed,
                            initCursorX,
                            initCursorY,
                            annotations,
                        },
                        this.onChangeAnnotations,
                    );
                    break;
                case 2:
                    let newItem = {
                        type: 'path',
                        stroke: this.props.penColor,
                        uuid: uuid(),
                        data: this.state.currentPath,
                        page: this.props.page,
                    };
                    annotations.push(newItem);

                    this.setState({ isMousePressed, annotations }, this.onChangeAnnotations);
                    break;
                case 3:
                    this.setState({ isNoteEditing: true, isMousePressed });
                    break;
                default:
                    this.setState({ isMousePressed });
                    break;
            }
        }
    };

    addNote = (x, y, text, id) => {
        let annotations = this.state.annotations;
        let scale = this.props.scale;
        x = x / scale;
        y = y / scale;
        let newComment = {
            type: 'comment',
            x,
            y,
            content: text,
            page: this.props.page,
        };

        if (id) {
            annotations.map(item => {
                if (item.uuid === id) {
                    item.content = text;
                    newComment.uuid = id;
                }
                return item;
            });
        } else {
            newComment.uuid = uuid();
            annotations.push(newComment);
        }

        this.setState({ annotations }, this.onChangeAnnotations);
    };

    // region moving
    pageOnMouseMove = e => {
        let clientX = e.clientX;
        let clientY = e.clientY;
        this.movingCursor(clientX, clientY);
    };

    // region moving
    pageOnTouchMove = e => {
        // console.log('PDFAnno pageOnTouchMove', e);
        let clientX = e.nativeEvent.touches[0].clientX;
        let clientY = e.nativeEvent.touches[0].clientY;
        this.movingCursor(clientX, clientY);
    };

    movingCursor = (clientX, clientY) => {
        let scale = this.props.scale;
        let page = document.getElementById('pageContainer');
        let cursorX = clientX - page.getBoundingClientRect().left;
        let cursorY = clientY - page.getBoundingClientRect().top;

        if (
            (this.state.isMousePressed && !this.state.selectedItem) ||
            ((this.state.resizing || this.state.resizingLeft) && this.state.selectedItem)
        ) {
            if (this.state.resizing) {
                this.resizeRect(this.state.selectedItem.uuid, cursorX / scale, cursorY / scale);
            } else if (this.state.resizingLeft) {
                this.resizeLeftRect(this.state.selectedItem.uuid, cursorX / scale, cursorY / scale);
            } else {
                let currentPath = this.state.currentPath;
                if (this.state.currentTool === 2) {
                    currentPath +=
                        ' l ' +
                        ((cursorX - this.state.cursorX) / scale || 0) +
                        ' ' +
                        ((cursorY - this.state.cursorY) / scale || 0);
                }
                this.setState({ cursorX, cursorY, currentPath });
            }
        }
    };

    getItem = item => {
        if (!item.attrs.uuid && item.parent) {
            return this.getItem(item.parent);
        } else {
            return item.attrs;
        }
    };

    // public method
    selectItem = item => {
        this.setState({ selectedItem: item });
    };


    canvasOnClick = e => {
        this.canvasClick = true;
        let item = e.target.attrs;

        let rect = new Konva.Rect({
            width: item.width,
            height: item.height,
            x: e.evt.x,
            y: e.evt.y
        });

        if (item && item.type === 'delete') {
            this.deleteAnnotationByUUID(e.target.attrs.uuid);
        } else if (
            item.type === 'area' &&
            this.state.currentTool !== 2 &&
            this.state.currentTool !== 3
        ) {
            this.setState({ selectedItem: item });
        } else if (item.type === 'path' && !this.state.currentTool) {
            this.setState({ selectedItem: item, pathWrapper: rect });
        } else if (item.type === 'comment') {
            let wrapItem = this.getItem(e.target);
            // console.log('wrapItem', e.target, wrapItem);
            this.setState({ selectedItem: wrapItem });
        } else if (item.type === 'resize') {
            // console.log('resize');
            this.setState({ resizing: true });
        } else if (item.type === 'resizeLeft') {
            this.setState({ resizingLeft: true });
        } else if (
            !this.state.resizing &&
            !this.state.resizingLeft &&
            item.type !== 'edit' &&
            item.type !== 'resize' &&
            item.type !== 'resizeLeft'
        ) {
            this.setState({ selectedItem: null });
        } else if (item.type === 'edit') {
            this.setState({ isNoteEditing: true });
        }
    };

    onClickNote = e => {
        if (e.target.attrs.type === 'edit') {
            // console.log('PDFAnno onClickNote edit', e.target);
            this.setState({ isNoteEditing: true });
        } else {
            this.setState({ selectedItem: this.getItem(e.target) });
        }
    };

    canvasOnMouseDown = e => {
        // console.log('PDFAnno canvasOnMouseDown');
        this.canvasClick = true;
        if (e.target.attrs.type === 'resize') {
            // console.log('resize');
            this.setState({ resizing: true });
        } else if (e.target.attrs.type === 'resizeLeft') {
            // console.log('resizeLeft');
            this.setState({ resizingLeft: true });
        }
    };

    drawing = () => {
        let scale = this.props.scale;
        if (
            this.state.currentTool === 1 &&
            this.state.isMousePressed &&
            !this.state.resizing &&
            !this.state.resizingLeft
        ) {
            let x = this.state.initCursorX;
            let y = this.state.initCursorY;
            let width = this.state.cursorX - x;
            let height = this.state.cursorY - y;

            return (
                <Rect
                    x={this.state.initCursorX / scale}
                    y={this.state.initCursorY / scale}
                    width={width / scale}
                    height={height / scale}
                    fill={this.props.highlightColor}
                    stroke="blue"
                    strokeWidht={1}
                    dash={[10, 5]}
                />
            );
        }

        if (this.state.currentTool === 2 && this.state.isMousePressed) {
            return <Path stroke={this.props.penColor} data={this.state.currentPath} />;
        }

        if (this.state.currentTool === 3 && this.state.isNoteEditing && !this.state.selectedItem) {
            return this.renderNote(
                this.state.initCursorX - 10,
                this.state.initCursorY - 10,
                uuid(),
                true,
            );
        }
    };

    onDragEnd = e => {
        // console.log('PDFAnno onDragEnd');
        this.setState({ isNoteEditing: false });
        if (!this.state.selectedItem || !this.selectedNode) return;
        this.canvasClick = true;
        let difX = this.selectedNode.attrs.x;
        let difY = this.selectedNode.attrs.y;
        let selectedItem = this.state.selectedItem;
        this.setItemPosition(selectedItem, difX, difY);
    };

    setItemPosition = (selectedItem, difX, difY) => {
        // console.log('PDFAnno setItemPosition', selectedItem, difX, difY);
        let annotations = this.state.annotations;
        let pathWrapper = this.state.pathWrapper;
        annotations = annotations.map(item => {
            if (item.uuid === selectedItem.uuid) {
                // console.log('PDFAnno ', item.uuid,  selectedItem.uuid, item.type === "comment");
                if (item.type === 'area') {
                    item.x = selectedItem.x + difX;
                    item.y = selectedItem.y + difY;
                    selectedItem.x = item.x;
                    selectedItem.y = item.y;
                } else if (item.type === 'path') {
                    // console.log('selectedItem', selectedItem);
                    let curX = item.data.split(' ')[1];
                    let curY = item.data.split(' ')[2];

                    let x = Number(curX) + difX;
                    let y = Number(curY) + difY;

                    item.data = item.data.replace(/^(.*? ){3}/, 'M ' + x + ' ' + y + ' ');
                    selectedItem.data = item.data;
                    pathWrapper.x = pathWrapper.x + difX;
                    pathWrapper.y = pathWrapper.y + difY;
                } else if (item.type === 'comment') {
                    // console.log('PDFAnno note', item);
                    item.x = selectedItem.x + difX;
                    item.y = selectedItem.y + difY;
                    selectedItem.x = item.x;
                    selectedItem.y = item.y;
                }
            }
            return item;
        });

        this.setState({ annotations, selectedItem: null, pathWrapper }, this.onChangeAnnotations);
        this.setState({ selectedItem });
    };

    /**
     *  resizing by mooving right-bottom circle
     * @param uuid
     * @param x
     * @param y
     */
    resizeRect = (uuid, x, y) => {
        let annotations = this.state.annotations;
        let selectedItem = this.state.selectedItem;
        annotations = annotations.map(item => {
            if (item.uuid === uuid) {
                let width = item.width;
                let height = item.height;
                let newX = item.x;
                let newY = item.y;
                let widthDif = x - item.x;
                let heightDif = y - item.y;

                if (widthDif >= 0) {
                    width = widthDif;
                } else {
                    newX = x;
                    width = item.x - x;
                }

                if (heightDif >= 0) {
                    height = heightDif;
                } else {
                    newY = y;
                    height = item.y - y;
                }

                item.width = width;
                item.height = height;
                item.x = newX;
                item.y = newY;
                selectedItem.width = item.width;
                selectedItem.height = item.height;
                selectedItem.x = item.x;
                selectedItem.y = item.y;
            }
            return item;
        });

        this.setState({ annotations });
    };

    /**
     *  resizing by mooving left-bottom circle
     * @param uuid
     * @param x
     * @param y
     */
    resizeLeftRect = (uuid, x, y) => {
        let annotations = this.state.annotations;
        let selectedItem = this.state.selectedItem;
        annotations = annotations.map(item => {
            if (item.uuid === uuid) {
                let newWidth = item.width - (x - item.x);
                let newHeight = y - item.y;
                item.x = x;
                item.height = newHeight > 1 ? newHeight : 1;
                item.width = newWidth > 1 ? newWidth : 1;
                selectedItem.width = item.width;
                selectedItem.height = item.height;
                selectedItem.x = item.x;
                selectedItem.y = item.y;
            }
            return item;
        });

        this.setState({ annotations });
    };

    renderAnnotations = () => {
        let { selectedItem, annotations } = this.state;
        return annotations.map((item, i) => {
            let fill = item.fill;
            // console.log('PDFAnno item', item);
            // let transformedItem = this.scaleTransform(item, this.props.scale, this.props.rotate);

            let { x, y, width, height, data } = item;

            if (selectedItem && item.uuid === selectedItem.uuid) {
                if (item.type === 'area') {
                    return (
                        <Group
                            key={i}
                            draggable={!this.state.resizing && !this.state.resizingLeft}
                            uuid={item.uuid}
                            dragOnTop={true}
                            onDragend={this.onDragEnd}
                            ref={el => (this.selectedNode = el)}
                        >
                            <Rect
                                // draggable={true}
                                x={item.x}
                                y={item.y}
                                width={item.width}
                                height={item.height}
                                cornerRadius={2}
                                uuid={item.uuid}
                                key="main"
                                stroke="blue"
                                strokeWidht={1}
                                fill={fill}
                                dash={[10, 5]}
                                type="area"
                            />
                            <Group>
                                <Circle
                                    uuid={item.uuid}
                                    key="c1"
                                    x={item.x + item.width}
                                    y={item.y}
                                    radius={15}
                                    fill="#ddd"
                                    stroke="#666"
                                    type="delete"
                                />
                                <Line
                                    x={item.x + item.width}
                                    y={item.y}
                                    points={[-7, -7, 7, 7]}
                                    stroke="#666"
                                    uuid={item.uuid}
                                    type="delete"
                                />
                                <Line
                                    x={item.x + item.width}
                                    y={item.y}
                                    points={[7, -7, -7, 7]}
                                    stroke="#666"
                                    uuid={item.uuid}
                                    type="delete"
                                />
                            </Group>
                            <Circle
                                draggable={false}
                                type="resize"
                                key="c2"
                                x={item.x + item.width}
                                y={item.y + item.height}
                                radius={10}
                                fill="#ddd"
                                stroke={this.state.resizing ? 'blue' : '#666'}
                            />
                            <Circle
                                draggable={false}
                                type="resizeLeft"
                                key="c3"
                                x={item.x}
                                y={item.y + item.height}
                                radius={10}
                                fill="#ddd"
                                stroke={this.state.resizingLeft ? 'blue' : '#666'}
                            />
                        </Group>
                    );
                }

                if (item.type === 'path') {
                    let { x, y, width, height } = this.state.pathWrapper;
                    return (
                        <Group
                            key={i}
                            draggable={true}
                            uuid={item.uuid}
                            dragOnTop={true}
                            onDragend={this.onDragEnd}
                            ref={el => (this.selectedNode = el)}
                        >
                            <Rect
                                type="area"
                                uuid={item.uuid}
                                stroke="blue"
                                strokeWidht={1}
                                fill="transparent"
                                dash={[10, 5]}
                                width={width}
                                height={height}
                                x={x}
                                y={y}
                            />
                            <Group>
                                <Circle
                                    uuid={item.uuid}
                                    key="c1"
                                    x={x + width}
                                    y={y}
                                    radius={15}
                                    fill="#ddd"
                                    stroke="#666"
                                    type="delete"
                                />
                                <Line
                                    x={x + width}
                                    y={y}
                                    points={[-7, -7, 7, 7]}
                                    stroke="#666"
                                    uuid={item.uuid}
                                    type="delete"
                                />
                                <Line
                                    x={x + width}
                                    y={y}
                                    points={[7, -7, -7, 7]}
                                    stroke="#666"
                                    uuid={item.uuid}
                                    type="delete"
                                />
                            </Group>
                            <Path
                                type="path"
                                uuid={item.uuid}
                                stroke="blue"
                                key={i}
                                data={item.data}
                            />
                        </Group>
                    );
                }

                if (item.type === 'comment') {
                    return (
                        <Group
                            key={i}
                            draggable={true}
                            uuid={item.uuid}
                            dragOnTop={true}
                            onDragend={this.onDragEnd}
                            ref={el => (this.selectedNode = el)}
                        >
                            {this.renderNote(item.x, item.y, item.uuid, true)}
                        </Group>
                    );
                }
            }
            if (this.props.page === item.page) {
                if (item.type === 'area') {
                    return (
                        <Rect
                            type="area"
                            uuid={item.uuid}
                            key={i}
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={fill}
                        />
                    );
                }
                if (item.type === 'path') {
                    return (
                        <Path
                            type="path"
                            uuid={item.uuid}
                            stroke={item.stroke}
                            key={i}
                            data={data}
                        />
                    );
                }
                if (item.type === 'comment') {
                    return this.renderNote(x, y, item.uuid, false);
                }
            }
            return null;
        });
    };

    changeCurrentTool = (e: Event) => {
        // console.log('PDFAnno changeCurrentTool', e);
        let tool = null;
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            tool = e.target.dataset.type;
        }

        switch (tool) {
            case 'highlight':
                this.setState({
                    currentTool: this.state.currentTool === 1 ? null : 1,
                    initCursorX: 0,
                    initCursorY: 0,
                    isMousePressed: false,
                    selectedItem: null,
                });
                break;
            case 'pen':
                this.setState({
                    currentTool: this.state.currentTool === 2 ? null : 2,
                    initCursorX: 0,
                    initCursorY: 0,
                    isMousePressed: false,
                    selectedItem: null,
                });
                break;
            case 'comment':
                this.setState({
                    currentTool: this.state.currentTool === 3 ? null : 3,
                    initCursorX: 0,
                    initCursorY: 0,
                    isMousePressed: false,
                    selectedItem: null,
                });
                break;
            default:
                // console.log('PDFAnno changeCurrentTool default');

                this.setState({
                    currentTool: null,
                    initCursorX: 0,
                    initCursorY: 0,
                    isMousePressed: false,
                    selectedItem: null,
                });
                break;
        }
    };

    addDrawingCursor() {
        let layer = document.getElementById('pageContainer');
        layer.classList.add('cursor-drawing');
        layer.addEventListener('touchmove', this.noMove, false);
    }

    removeDrawingCursor() {
        let layer = document.getElementById('pageContainer');
        layer.classList.remove('cursor-drawing');
        layer.removeEventListener('touchmove', this.noMove, false);
    }

    noMove = e => {
        e.preventDefault();
    };

    saveNote = e => {
        e.preventDefault();
        // console.log('PDFAnno saveNote', this.noteInput.value);
        let uuid = null;
        let selectedItem = this.state.selectedItem;
        if (selectedItem) {
            uuid = selectedItem.uuid;
        }
        this.addNote(
            this.state.initCursorX - 10,
            this.state.initCursorY - 10,
            this.noteInput.value,
            uuid,
        );
        this.setState({ isNoteEditing: false, selectedItem: null });
    };

    cancelNote = e => {
        e.preventDefault();
        // console.log('PDFAnno cancelNote', e);
        this.setState({
            isNoteEditing: false,
            // selectedItem: this.state.currentTool === 3 ? null : this.state.selectedItem, // if we want to save the selection
            selectedItem: null, // if we want to clear selection
        });
    };

    getTextByUUID = uuid => {
        return this.state.annotations.filter(item => item.uuid === uuid)[0].content;
    };

    /**
     *  print PDF in frame window
     */
    printDocument = () => {
        // console.log('DocumentViewer printDocument', this.state.blob);
        this.setState({ showPDF: true });
        setTimeout(() => window.frames['my-frame'].print(), 100);
    };

    download = () => {
        let save = document.createElement('a');
        save.href = this.props.blob;
        save.download = this.props.fileName + '.pdf';
        save.target = '_blank';
        document.body.appendChild(save);
        save.click();
        document.body.removeChild(save);
    };

    clearAllAnnotations = () => {
        this.setState({ showClearAllModal: true} )
    };

    clear = () =>{
        this.setState({ annotations: [] }, this.onChangeAnnotations);
        this.closeClearAllModal()
    }

    closeClearAllModal = () => {
        this.setState({ showClearAllModal: false})
    }

    renderNote = (x, y, id, selected = false) => {
        let scale = this.props.scale;
        if (this.state.isNoteEditing && selected) {
            // console.log('PDFAnno renderNote', selected, id);
            // need to apply only on the new note
            x = x / scale;
            y = y / scale;
        }
        let strokeColor = selected ? 'black' : 'gray';
        return (
            <Group
                dragOnTop={true}
                uuid={id}
                type="comment"
                x={x}
                y={y}
                width={20}
                height={20}
                key={x + ' ' + y}
                onClick={this.onClickNote}
            >
                {selected &&
                    !this.state.isNoteEditing && (
                        <Group y={-40} x={-15} type="edit">
                            <Rect
                                width={56}
                                height={30}
                                fill="#f8f8f8"
                                cornerRadius={2}
                                shadowColor="rgb(79, 80, 86)"
                                shadowBlur={2}
                                shadowOpacity={0.75}
                                shadowOffsetX={0.5}
                                shadowOffsetY={0.5}
                                type="edit"
                            />
                            <Text
                                type="edit"
                                y={7}
                                fontSize={16}
                                width={56}
                                height={35}
                                text="Edit"
                                align="center"
                                fill="rgb(79, 80, 86)"
                            />
                        </Group>
                    )}
                <Path
                    type="comment"
                    stroke={strokeColor}
                    fill="yellow"
                    data="M 0 3C 0 1.34315 1.34315 0 3 0L 28 0C 29.6569 0 31 1.34315 31 3L 31 19.2222C 31 20.8791 29.6569 22.2222 28 22.2222L 21.0357 22.2222L 15.5 30L 9.96429 22.2222L 3 22.2222C 1.34315 22.2222 0 20.8791 0 19.2222L 0 3Z"
                />
                <Path
                    type="comment"
                    stroke={strokeColor}
                    data="M 0 0L 20 0L 20 -1L 0 -1L 0 0Z"
                    y={6}
                    x={6}
                />
                <Path
                    type="comment"
                    stroke={strokeColor}
                    data="M 0 0L 20 0L 20 -1L 0 -1L 0 0Z"
                    y={11}
                    x={6}
                />
                <Path
                    type="comment"
                    stroke={strokeColor}
                    data="M 0 0L 20 0L 20 -1L 0 -1L 0 0Z"
                    y={16}
                    x={6}
                />
            </Group>
        );
    };

    renderEditNoteWindow = () => {
        if (this.state.isNoteEditing) {
            let selectedItem = this.state.selectedItem;
            let defaultText = '';
            if (selectedItem) {
                defaultText = this.getTextByUUID(selectedItem.uuid);
            }

            return (
                <div className="pdf-note">
                    <div className="pdf-fade" />
                    <div className="pdf-note-edit">
                        <form>
                            {/* <div className="pdf-note-edit-title">Create note</div> */}
                            <div className="pdf-note-edit-body">
                                <textarea
                                    defaultValue={defaultText}
                                    autoFocus={true}
                                    ref={el => (this.noteInput = el)}
                                    name="notes"
                                    id="note"
                                    cols="30"
                                    rows="10"
                                    placeholder="Add your note here..."
                                />
                            </div>
                            <div className="pdf-note-edit-footer">
                                {selectedItem &&
                                    selectedItem.uuid && (
                                        <button
                                            onClick={this.deleteAnnotationByUUID.bind(
                                                this,
                                                selectedItem.uuid,
                                            )}
                                            className="btn btn-sm btn-danger mr-auto"
                                        >
                                            Delete
                                    </button>
                                    )}
                                <div>
                                    <button
                                        onClick={this.cancelNote}
                                        className="btn btn-sm btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        onClick={this.saveNote}
                                        className="btn btn-sm btn-primary ml-2"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            );
        }
    };

    renderMainView = () => {
        return (
            <div
                id="pageContainer"
                onMouseDown={this.pageOnMouseDown}
                onMouseUp={this.pageOnMouseUp}
                onMouseMove={this.pageOnMouseMove}
                onTouchStart={this.pageOnTouchStart}
                onTouchMove={this.pageOnTouchMove}
                onTouchEnd={this.pageOnMouseUp}
                style={{ transform: 'rotate(' + this.props.rotate + 'deg)' }}
            >
                {this.state.documentLoaded ? (
                    <Stage
                        width={this.state.documentWidth}
                        height={this.state.documentHeight}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                        }}
                        onClick={this.canvasOnClick}
                        onMouseDown={this.canvasOnMouseDown}
                        onTouchStart={this.canvasOnClick}
                        listening={true}
                    >
                        <Layer scale={{ x: this.props.scale, y: this.props.scale }}>
                            {this.renderAnnotations()}
                            {this.drawing()}
                        </Layer>
                    </Stage>
                ) : null}
            </div>
        );
    };

    render() {
        // console.log('PDFAnno render');
        if (this.props.blob) {
            return (
                <div className={this.props.className}>
                <ClearAllModal show={this.state.showClearAllModal} clearAll={() => this.clear()} close={() => this.closeClearAllModal()}/>
                    {this.props.children(this)}
                    {this.renderEditNoteWindow()}
                    {this.state.showPDF && (
                        <iframe
                            name="my-frame"
                            id="my-frame"
                            src={this.props.blob}
                            title="my-frame"
                            style={{
                                display: 'none',
                                position: 'absolute',
                                zIndex: 3000,
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    )}
                </div>
            );
        }
        return <div>Loading</div>;
    }
}

PdfAnno.displayName = 'react-pdf-anno';

export default PdfAnno;



// WEBPACK FOOTER //
// ./src/containers/PrivateArea/DocumentViewer/src/PDFAnno.js